// Fetches a page of admins. Parser runs inside `queryFn` so containers
// always see the view model, never the raw DTO. Filter facets (status,
// role, invitationStatus, mfa) are part of the params and therefore part
// of the cache key — the same filter combination hits the same cache row.

import { useQuery } from '@tanstack/vue-query'
import { toValue, type MaybeRefOrGetter } from 'vue'

import { adminsQueryKeys } from '../adminsQueryKeys'
import { getAdminsList } from '../adminsEndpoints'
import { parseAdmin } from '../adminParser'
import type { Admin, GetAdminsListParams } from '../../types/adminTypes'
import type { PaginatedResponse } from '@shared/types'

export function useAdminsListQuery(params: MaybeRefOrGetter<GetAdminsListParams>) {
  return useQuery({
    queryKey: () => adminsQueryKeys.list(toValue(params)),
    queryFn: async (): Promise<PaginatedResponse<Admin>> => {
      const dto = await getAdminsList(toValue(params))
      const page = dto.page ?? toValue(params).page ?? 1
      const limit = dto.limit ?? toValue(params).limit ?? 10
      const totalItems = dto.total
      return {
        data: dto.rows.map(parseAdmin),
        pagination: {
          page,
          limit,
          totalItems,
          totalPages: limit > 0 ? Math.ceil(totalItems / limit) : 0,
        },
      }
    },
  })
}
